import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { z } from 'zod';

export const useQueryParam = (param: string) => {
  const location = useLocation();
  return getQueryParam(location.search, param);
};

export const getQueryParam = (searchParams: string, paramName: string) => {
  const queryParams = new URLSearchParams(searchParams);
  return queryParams.get(paramName);
};

export const useQueryParams = <Output extends object, Input = Output>(schema: z.ZodType<Output, Input>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateQueryParams = useCallback(
    (params: Partial<z.infer<typeof schema>>) => {
      setSearchParams(
        prevSearchParams => {
          const updatedSearchParams = new URLSearchParams(prevSearchParams);
          Object.entries(params).forEach(([key, value]) => {
            updatedSearchParams.set(key, String(value));
          });

          return updatedSearchParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setQueryParams = useCallback(
    (params: z.infer<typeof schema>) => {
      const newSearchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        newSearchParams.set(key, String(value));
      });

      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams],
  );

  const queryParams = useMemo(
    () => schema.parse(Object.fromEntries(searchParams.entries())),
    [schema, searchParams],
  );

  return { queryParams, setQueryParams, updateQueryParams };
};

type UseQueryParamStateOptions<T extends string> = {
  key: string;
  defaultValue: T;
  allowedValues: T[];
};

export const useQueryParamState = <T extends string>({
  key,
  defaultValue,
  allowedValues,
}: UseQueryParamStateOptions<T>): [T, (value: T) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsValue = searchParams.get(key);
  const value = (allowedValues as unknown[]).includes(searchParamsValue)
    ? (searchParamsValue as T)
    : defaultValue;

  const setValue = useCallback(
    (newValue: string) => {
      setSearchParams(
        prevSearchParams => {
          const updatedSearchParams = new URLSearchParams(prevSearchParams);
          updatedSearchParams.set(key, newValue);
          return updatedSearchParams;
        },
        { replace: true },
      );
    },
    [key, setSearchParams],
  );

  return [value, setValue];
};
